import React, { useState } from 'react';
import { Colors } from 'library';
import { Chip, IconButton, Row, Stack, Text, Title } from 'library/ui';
import QuestionMark from '@mui/icons-material/QuestionMark';
import { ArrowForward } from '@mui/icons-material';
import { formatMonthDayYear } from 'shared/Dates';
import { BasicCard } from './BasicCard';
import { WorkerTab } from './WorkerTab';
import type { ActionNeededWorker } from 'parent-portal/home/new/types';
import ComplianceActionInfoModal from 'parent-portal/jobs/upcoming/BusinessCalendar/ComplianceActions/ComplianceActionInfoModal';

const HELP_ARTICLE_URL =
    'https://chatwoot.join-tandem.com/hc/child-care-center-help-center/articles/1742826037-what-if-my-booked-worker-has-action-required-next-to-their-name-learn-more-about-initiating-resubmissions-in-clearinghouse';

export function ActionNeededWorkers({
    loading,
    workers,
    refresh,
}: {
    loading: boolean;
    workers?: ActionNeededWorker[];
    refresh: () => void;
}) {
    const [selectedWorker, setSelectedWorker] = useState<ActionNeededWorker>();

    function onClick(value: ActionNeededWorker) {
        setSelectedWorker(value);
    }

    const noWorkers = !workers || workers.length === 0;
    if (noWorkers) {
        return null;
    }

    return (
        <BasicCard loading={loading}>
            <Stack gap="md">
                <Row justify="between" align="start" gap="sm">
                    <Stack gap="none">
                        <Title size="h2">Action Needed</Title>
                        <Text size="sm" tone="secondary">
                            Please complete the below actions to ensure your program remains in compliance.
                        </Text>
                    </Stack>
                    <IconButton
                        icon={QuestionMark}
                        onClick={() => window.open(HELP_ARTICLE_URL, '_blank', 'noreferrer')}
                    />
                </Row>

                <Stack gap="sm">
                    {workers.map((worker) => (
                        <WorkerTab
                            key={worker.user.id}
                            worker={{
                                user: worker.user,
                                profile_image: worker.user.profile_picture,
                                middle: (
                                    <>
                                        Booked at {worker.business_location.name} starting{' '}
                                        <b>{formatMonthDayYear(worker.shift_date)}</b>
                                    </>
                                ),
                                bottom: (
                                    <Chip variant="filled" bgColor={Colors.error} textColor={Colors.white} size="small">
                                        {worker.action.replaceAll('_', ' ').startCase()}
                                    </Chip>
                                ),
                            }}
                            onClick={() => onClick(worker)}
                            icon={ArrowForward}
                        />
                    ))}
                </Stack>
            </Stack>

            {selectedWorker ? (
                <ComplianceActionInfoModal
                    provider_user={selectedWorker?.user}
                    business_location_id={selectedWorker?.business_location.id}
                    actionText={selectedWorker?.action}
                    modalOpen={Boolean(selectedWorker)}
                    setModalOpen={() => setSelectedWorker(undefined)}
                    refresh={refresh}
                />
            ) : null}
        </BasicCard>
    );
}
